/**
 * builderUtils - State conversion between builder UI and profile data
 *
 * The builder works with a visual state (categories with ordered items).
 * These utilities convert between that state and the profile's modules
 * array + sidebar override JSON.
 */

/**
 * Convert resolved sidebar + profile data into builder state
 *
 * Builder state shape:
 * {
 *   categories: [
 *     { id: 'cat-overview', code: 'overview', label: 'Vue d\'ensemble', items: ['dashboard', 'alerts'] },
 *     { id: 'cat-exploitation', code: 'exploitation', label: 'Exploitation', items: ['vehicles', 'drivers'] },
 *   ],
 *   unassigned: ['analytics', 'radar']  // features not in any category
 * }
 */
export function profileDataToBuilderState(profile, resolvedSidebar, allFeatures) {
  const categories = [];
  const assignedIds = new Set();

  if (resolvedSidebar && typeof resolvedSidebar === 'object') {
    // Build from resolved sidebar groups
    const groups = Array.isArray(resolvedSidebar)
      ? resolvedSidebar
      : Object.entries(resolvedSidebar).map(([code, group]) => ({
          code,
          ...group,
        }));

    groups.forEach((group, idx) => {
      const code = group.code || group.group_code || `group_${idx}`;
      const items = (group.items || []).map(item =>
        typeof item === 'string' ? item : (item.id || item.view_code)
      );
      items.forEach(id => assignedIds.add(id));

      categories.push({
        id: `cat-${code}`,
        code,
        label: group.label || code,
        items,
      });
    });
  } else if (profile && Array.isArray(profile.modules)) {
    // No resolver output — build single "all modules" category
    categories.push({
      id: 'cat-all',
      code: 'all',
      label: 'Modules',
      items: [...profile.modules],
    });
    profile.modules.forEach(id => assignedIds.add(id));
  }

  // Unassigned = all features minus what's already in categories
  const profileModules = profile?.modules || [];
  const unassigned = allFeatures
    .filter(f => !assignedIds.has(f.id))
    .filter(f => profileModules.includes(f.id))
    .map(f => f.id);

  // Pool = all features NOT selected at all (not in modules)
  const pool = allFeatures
    .filter(f => !assignedIds.has(f.id) && !profileModules.includes(f.id))
    .map(f => f.id);

  return {
    categories,
    unassigned, // selected but not placed in categories
    pool, // not selected at all
  };
}

/**
 * Convert builder state back to profile data (modules + layout override).
 *
 * WYSIWYG: The builder IS the sidebar. What you see is what users get.
 *
 * Generates a `full_layout` override — the resolver uses it directly
 * instead of merging patches onto DB groups. This supports:
 * - Custom categories (not in nav_groups DB)
 * - Arbitrary item placement (any item in any group)
 * - Complete control over group order, naming, and contents
 */
export function builderStateToProfileData(state, defaultGroups) {
  // Build full_layout: the complete sidebar structure
  const fullLayout = state.categories
    .filter(cat => cat.items.length > 0) // Skip empty categories
    .map((cat, idx) => ({
      code: cat.code,
      label: cat.label,
      order: (idx + 1) * 10,
      items: [...cat.items],
    }));

  const override = fullLayout.length > 0 ? { full_layout: fullLayout } : null;

  // Collect all items placed in categories + unassigned = the active modules
  const modules = [];
  state.categories.forEach(cat => {
    cat.items.forEach(id => {
      if (!modules.includes(id)) modules.push(id);
    });
  });
  // Also include unassigned items (selected but not placed in a specific category)
  (state.unassigned || []).forEach(id => {
    if (!modules.includes(id)) modules.push(id);
  });

  return {
    modules: modules.length > 0 ? modules : null,
    override,
  };
}

/**
 * Compute a minimal override patch (only differences from DB defaults)
 */
export function computeOverridePatch(state, defaults) {
  // For now, delegate to builderStateToProfileData
  return builderStateToProfileData(state, defaults).override;
}

/**
 * Generate unique ID for new categories
 */
let catCounter = 0;
export function generateCategoryId() {
  catCounter++;
  return `cat-custom-${Date.now()}-${catCounter}`;
}

/**
 * Move an item between containers in builder state
 * Returns a new state object (immutable)
 */
export function moveItem(state, itemId, fromContainerId, toContainerId, toIndex) {
  const newState = {
    categories: state.categories.map(c => ({ ...c, items: [...c.items] })),
    unassigned: [...(state.unassigned || [])],
    pool: [...(state.pool || [])],
  };

  // Remove from source
  if (fromContainerId === 'pool') {
    newState.pool = newState.pool.filter(id => id !== itemId);
  } else if (fromContainerId === 'unassigned') {
    newState.unassigned = newState.unassigned.filter(id => id !== itemId);
  } else {
    const sourceCat = newState.categories.find(c => c.id === fromContainerId);
    if (sourceCat) {
      sourceCat.items = sourceCat.items.filter(id => id !== itemId);
    }
  }

  // Add to target
  if (toContainerId === 'pool') {
    newState.pool.push(itemId);
  } else if (toContainerId === 'unassigned') {
    newState.unassigned.push(itemId);
  } else {
    const targetCat = newState.categories.find(c => c.id === toContainerId);
    if (targetCat) {
      const idx = toIndex != null ? toIndex : targetCat.items.length;
      targetCat.items.splice(idx, 0, itemId);
    }
  }

  return newState;
}

/**
 * Reorder items within a category
 */
export function reorderItems(state, containerId, oldIndex, newIndex) {
  const newState = {
    categories: state.categories.map(c => ({ ...c, items: [...c.items] })),
    unassigned: [...(state.unassigned || [])],
    pool: [...(state.pool || [])],
  };

  const cat = newState.categories.find(c => c.id === containerId);
  if (!cat) return state;

  const [removed] = cat.items.splice(oldIndex, 1);
  cat.items.splice(newIndex, 0, removed);

  return newState;
}

/**
 * Reorder categories
 */
export function reorderCategories(state, oldIndex, newIndex) {
  const newCategories = [...state.categories];
  const [removed] = newCategories.splice(oldIndex, 1);
  newCategories.splice(newIndex, 0, removed);

  return {
    ...state,
    categories: newCategories,
  };
}
